import { cloneDeep } from 'lodash';

import { ComposerRowData, FormSchemaItem, RowItem } from './composer-grid.models';

export function normalizeKey(raw: string): string {
  return raw.replace('$', '.').split('.').pop()!;
}

function extractDate(displayValue: string): string {
  return displayValue.split(' ')[0];
}

function mapSelectBoolean(rawVal: any, enumList?: Array<{ name: string; value: any }>): string {
  const val = typeof rawVal === 'boolean' ? String(rawVal) : rawVal;
  if (!enumList) return val;
  const match = enumList.find((e) => e.value === val);
  return match ? match.name : val;
}

function buildCheckboxFieldSet(formSchema: FormSchemaItem[]): Set<string> {
  return new Set(
    formSchema
      .filter((f) => f.formtype === 'checkbox')
      .map((f) => normalizeKey(f.key))
  );
}

function buildSelectBooleanMap(formSchema: FormSchemaItem[]): Record<string, FormSchemaItem> {
  return formSchema
    .filter((f) => f.formtype === 'select_boolean')
    .reduce((map, form) => {
      map[normalizeKey(form.key)] = form;
      return map;
    }, {} as Record<string, FormSchemaItem>);
}

function buildRowData(
  items: RowItem[],
  selectBoolMap: Record<string, FormSchemaItem>,
  checkboxFields: Set<string>,
  actions: { edit: boolean; delete: boolean; view: boolean },
  formSchema: FormSchemaItem[]
): ComposerRowData {
  const rowData: any = {
    row: items,
    actions,
    formSchema,
    editedCell: []
  };

  for (const item of items) {
    const field = normalizeKey(item.key);

    if (item.link) {
      rowData[`Link_${item.label}`] = item.link;
    }

    let cellValue: any;
    if ('closest_match_fit_score'.includes(field)) {
      if (item.display_value === 'Unknown' || item.value == null) {
        cellValue = '';
      } else {
        cellValue = item.display_value;
      }
    } else if (['is_private', 'outputGp', 'rid'].includes(field)) {
      cellValue = item.rid ?? item.value;
    } else if (['valid_from', 'valid_to', 'created_on'].includes(field)) {
      cellValue = extractDate(item.display_value);
    } else if (selectBoolMap[field]) {
      cellValue = mapSelectBoolean(item.value, selectBoolMap[field].enum);
    } else if (checkboxFields.has(field)) {
      cellValue = item.value === true || item.value === 'true';
    } else if (typeof item.value === 'number') {
      cellValue = item.value;
    } else {
      cellValue = item.display_value;
    }

    rowData[field] = cellValue;
  }

  rowData.originalData = cloneDeep(rowData);
  return rowData;
}

export function mapRows(
  lines: RowItem[][],
  formSchema: FormSchemaItem[],
  actions: { edit: boolean; delete: boolean; view: boolean }
): ComposerRowData[] {
  const selectBooleanMap = buildSelectBooleanMap(formSchema);
  const checkboxFields = buildCheckboxFieldSet(formSchema);

  return lines.map((rowItems) => buildRowData(rowItems, selectBooleanMap, checkboxFields, actions, formSchema));
}
